import { cn } from "@/lib/utils";
import { PARENT_LOGO, PARENT_MARK, darkLogoFor } from "@/lib/constants";

/**
 * One brand mark, drawn as the light cut and — where the artwork exists — the
 * dark cut behind it, with `dark:` picking between them.
 *
 * Both are in the DOM and CSS chooses, rather than JS reading the resolved
 * theme: `useTheme()` has no answer on the first paint, so a JS swap shows the
 * wrong mark for a frame every time the app loads in dark. It also keeps light
 * mode exactly what it was — the dark copy is `hidden` there and paints
 * nothing.
 *
 * `onDark` is for the surfaces that are dark in BOTH themes. The theme is not
 * the question a mark actually has — the question is what it is sitting on —
 * and the sidebar plane is navy whether or not the app is. Without this the
 * drawer's header drew the navy-ink artwork on navy in light mode, which is
 * the same "ink on ink" the dark cuts exist to solve.
 *
 * Exported because the lockup is not the only place a tenant's mark is drawn:
 * the top nav's workspace switcher lists every tenant the user belongs to, and
 * a mark in that menu has the same theme problem as a mark in the header. One
 * resolution, both places.
 */
export const BrandMark = ({
  src,
  alt,
  className,
  onDark = false,
}: {
  src: string;
  alt: string;
  className: string;
  onDark?: boolean;
}) => {
  const dark = darkLogoFor(src);
  if (!dark) return <img src={src} alt={alt} className={className} />;
  if (onDark) return <img src={dark} alt={alt} className={className} />;
  return (
    <>
      <img src={src} alt={alt} className={cn(className, "dark:hidden")} />
      <img src={dark} alt="" aria-hidden className={cn(className, "hidden dark:block")} />
    </>
  );
};

/**
 * Optical baseline correction for the inline label.
 *
 * The lockup reads as one phrase — "[XBG] | [Everon] Backlog" — so the label
 * has to sit on the same baseline as the wordmarks beside it. Centring cannot
 * put it there: a centred line box puts the baseline at
 * `centre - (ascent + descent)/2 + ascent`, and the font's ascent and descent
 * are not symmetric, so the text lands ~1.4px above the logos' baseline (both
 * PNGs are cropped to their own baseline and so sit flush with the bottom of
 * their box). Measured in the browser at the top nav's 18px/24px pairing:
 * label baseline 34.5px, XBG 36.0px, Everon 35.8px. This nudges the label the
 * one whole pixel that closes the gap without landing text on a half pixel.
 *
 * Exported because TopNav's tenant-switcher chevron sits beside the label and
 * has to travel with it — otherwise fixing the label re-opens the same gap
 * against the chevron.
 */
export const LABEL_BASELINE_NUDGE = "translate-y-px";

interface TenantWordmarkProps {
  /** The tenant's own logo path (from useTenant/useHostTenant), or null. */
  logo: string | null;
  /** The tenant display name — the label fallback when there is no logo. */
  name: string;
  /**
   * The tenant's slug (`useTenant()`/`useHostTenant()`'s `id`), used only to
   * detect "this is XBG's own tenant" so its mark isn't duplicated beside the
   * parent co-brand. Identity-based rather than comparing `logo` against a
   * fixed asset path, so it still holds after XBG uploads its own custom logo.
   * Optional for callers that only ever render a non-XBG tenant.
   */
  tenantId?: string;
  /** Typography classes for the label text, matching each call site's span. */
  className?: string;
  /**
   * "horizontal" (default) — logos inline with the label, for the sidebar / top
   * nav. "vertical" — logos stacked above a larger, centred label, for the login
   * hero, where the marks are also rendered larger.
   */
  orientation?: "horizontal" | "vertical";
  /**
   * Render only the co-brand marks, without the product label. The login screen
   * uses this in the card's partner cap — a small co-brand strip above the
   * "Backlog" hero — so it renders at the compact inline sizing (driven by
   * `orientation`), not the oversized hero sizing.
   */
  hideLabel?: boolean;
  /**
   * The mark is sitting on a surface that is dark in BOTH themes — the navy
   * sidebar plane, and so the mobile drawer's header. Picks the dark cut of
   * each mark outright instead of letting `dark:` decide.
   */
  onDark?: boolean;
  /**
   * The lockup has a width to hit rather than a width to take: the top bar's
   * brand zone is pinned to the sidebar's 240px so its closing rule lands on
   * the sidebar's right edge, and the full-size marks do not fit inside it.
   * One step down on both marks and one on the gaps, which is what the 240
   * costs. Everything else — the drawer header, the login lockup — keeps the
   * larger sizing, because nothing there is measured against a rail.
   */
  dense?: boolean;
}

/**
 * The tenant identity lockup shown wherever the product is branded.
 *
 * XBG is the parent company, so the muted XBG mark always leads; a non-XBG
 * tenant then adds a divider and its own identity — its logo when it has one
 * (`[XBG] | [EHP]`), otherwise its name as text (`[XBG] | Welcome`). When the
 * tenant IS XBG (`tenantId === "xbg"`) the parent is not repeated and the
 * divider is dropped — regardless of what XBG's own `logo` value happens to
 * be, so uploading a custom logo for XBG's own tenant never double-renders it.
 *
 * When a logo is present the label is the product noun "Backlog" — the marks
 * already carry the brand name, so we don't repeat it in text. With no logo we
 * fall back to the tenant name so a logo-less tenant (e.g. the welcome default)
 * is still named.
 */
export default function TenantWordmark({
  logo,
  name,
  tenantId,
  className,
  orientation = "horizontal",
  hideLabel = false,
  onDark = false,
  dense = false,
}: TenantWordmarkProps) {
  const isXbgTenant = tenantId === "xbg";
  const showTenantLogo = !!logo && !isXbgTenant;
  const label = logo ? "Backlog" : name;
  // The divider marks "there's a tenant beyond XBG here" — it belongs beside
  // whichever tenant identifier actually renders. That's the tenant logo when
  // there is one; otherwise (a logo-less tenant, e.g. the unconfigured
  // default) it's the text label, so the divider moves there instead. Neither
  // applies for the XBG tenant itself, or when the label is suppressed
  // entirely (hideLabel, with no tenant logo to divide from).
  const showLabelDivider = !hideLabel && !isXbgTenant && !showTenantLogo;
  // Only the explicit "vertical" orientation gets the larger stacked hero
  // sizing. Marks-only (hideLabel) follows its orientation like any other
  // caller — the login cap passes the default "horizontal" for compact marks.
  const vertical = orientation === "vertical";

  // Inline chrome uses the XBG WORDMARK (no tagline) so it is a single-line mark
  // that shares an optical centre with single-line tenant logos. It sits smaller
  // and muted as the umbrella brand under the full-size tenant mark — legible
  // now that the tiny tagline is cropped off. The login hero uses the full
  // stacked lockup, larger, where the tagline is big enough to read.
  // `brand-parent-mark` is a hook, not a look: the 60% opacity that makes the
  // umbrella brand recede on a near-white page makes it disappear on a dark
  // one, and the mark is supplied artwork that cannot be recoloured per theme.
  // index.css lifts it back inside the top nav's `.brand-zone`.
  const parentSrc = vertical ? PARENT_LOGO : PARENT_MARK;
  // `dense` is a width budget, not a second design: the same three parts in the
  // same order, each one step smaller. The tenant mark is the expensive one —
  // Everon's artwork is 4:1, so every pixel of height costs four of width — so
  // it takes the bigger step, and the row height follows it down (h-6 → h-5)
  // so the lockup's box still fits what is in it.
  const parentSize = vertical
    ? "brand-parent-mark h-10 w-auto opacity-60"
    : cn("brand-parent-mark w-auto opacity-60", dense ? "h-3.5" : "h-4");
  const tenantSize = vertical ? "h-12 w-auto" : cn("w-auto", dense ? "h-[18px]" : "h-6");
  const rowHeight = dense ? "h-5" : "h-6";

  // A real rule, not a "|" glyph, so its height is not capped by the font
  // size. Inline it spans the full height of the lockup row (h-6, the tenant
  // mark's height) rather than an in-between 20px that matched neither
  // neighbour and read as floating; the login lockup keeps its taller rule.
  // `brand-divider` is a hook, not a look: the top nav drops the umbrella mark
  // on a phone-width header, and the rule that separated it from the tenant's
  // own mark has to leave with it.
  const divider = (
    <span
      aria-hidden="true"
      className={cn("brand-divider w-px bg-muted-foreground/40", vertical ? "h-9" : rowHeight)}
    />
  );

  const marks = (
    <span className={cn("flex shrink-0 items-center", dense ? "gap-1.5" : "gap-2.5", !vertical && rowHeight)}>
      <BrandMark src={parentSrc} alt="XBG" className={parentSize} onDark={onDark} />
      {showTenantLogo && (
        <>
          {divider}
          <BrandMark src={logo} alt={name} className={tenantSize} onDark={onDark} />
        </>
      )}
    </span>
  );

  if (hideLabel) {
    return marks;
  }

  if (vertical) {
    return (
      <span className="flex flex-col items-center gap-3">
        {marks}
        <span className={className}>{label}</span>
      </span>
    );
  }

  // One 24px row — the tenant mark's height — with everything centred in it, so
  // the lockup's box is the size of what you can see. The label's own 28px line
  // box used to set the height instead, which made the switcher's clickable and
  // focus box 4px taller than any of its contents. Pinning the row to h-6 fixes
  // that; the nudge then puts the label on the marks' baseline.
  // `min-w-0` + a truncating label, because the lockup now lives inside a box
  // with a FIXED width (the top bar's brand zone, pinned to the sidebar's 240px)
  // and the label is the only part of it that varies: a logo-less tenant falls
  // back to its own name here, and an overlong one has to end in an ellipsis
  // rather than run out over the search field. The marks never squeeze — a
  // compressed logo is worse than a shortened word.
  return (
    <span className={cn("flex min-w-0 items-center", rowHeight, dense ? "gap-1.5" : "gap-2")}>
      {marks}
      {showLabelDivider && divider}
      {/* The 1px baseline nudge was measured at the roomy 18px/24px pairing;
          the dense top-bar lockup centres to the pixel without it (measured:
          label cy 28 vs marks cy 28), and WITH it the label rides 1px low. */}
      <span className={cn(!dense && LABEL_BASELINE_NUDGE, "truncate", className)}>{label}</span>
    </span>
  );
}
